/**
 * BillingContext — global billing state (account, balance, paywall).
 *
 * Responsibilities:
 *   1. Load the current user's billing account on mount and whenever auth changes.
 *   2. Expose `refresh()` so components can invalidate after a successful AI action.
 *   3. Expose `openPaywall()` / `closePaywall()` so a 402 interceptor or low-balance
 *      UI can surface the upgrade prompt from anywhere.
 *
 * Failure mode:
 *   - If the account endpoint errors (e.g. backend still deploying), we don't
 *     block the app — leave `account` null and `ready` true so the rest of the
 *     UI renders. The paywall simply can't be triggered until a fetch succeeds.
 *
 * Consumers:
 *   - CreditBalanceBadge (header widget)
 *   - PaywallPrompt (modal)
 *   - CheckoutPage (post-checkout refresh)
 *   - apiClient 402 interceptor (wires openPaywall via a module-level hook)
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import billingApi from '../services/billingApi';
import { useAuth } from './AuthContext';

const BillingContext = createContext(null);

// Module-level ref so non-React code (axios interceptors) can trigger the
// paywall without going through a hook. Set by BillingProvider on mount.
const _paywallHook = { open: () => {}, refresh: () => {} };

export function triggerPaywall(reason) {
  _paywallHook.open(reason);
}

export function triggerBalanceRefresh() {
  _paywallHook.refresh();
}

export function BillingProvider({ children }) {
  const { user, loading: authLoading } = useAuth();

  const [account, setAccount] = useState(null);
  const [ready, setReady] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallReason, setPaywallReason] = useState(null);

  // Track the last fetch so rapid successive refreshes coalesce. Otherwise a
  // user clicking "generate drawing" rapidly floods the backend with
  // /billing/account calls.
  const inflightRef = useRef(null);

  const refresh = useCallback(async () => {
    if (authLoading || !user) {
      return null;
    }
    if (inflightRef.current) {
      return inflightRef.current;
    }
    const p = (async () => {
      try {
        const res = await billingApi.getAccount();
        // Array.isArray guard isn't meaningful here (endpoint returns an object),
        // but defensively coerce null/non-object to null so downstream code sees
        // a consistent shape.
        const data = res.data && typeof res.data === 'object' ? res.data : null;
        setAccount(data);
        return data;
      } catch (err) {
        // Non-fatal — log and keep the previous account snapshot if any.
        // eslint-disable-next-line no-console
        console.warn('Billing account fetch failed:', err?.message || err);
        return null;
      } finally {
        inflightRef.current = null;
        setReady(true);
      }
    })();
    inflightRef.current = p;
    return p;
  }, [authLoading, user]);

  // Load on mount + whenever the user changes.
  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!user) {
      setAccount(null);
      setReady(true);
      return;
    }
    refresh();
  }, [authLoading, user, refresh]);

  // Keep the balance fresh without requiring components to invalidate manually
  // after every AI action:
  //   - Poll every 45s while the user is logged in and the tab is visible.
  //   - Refresh on window focus so returning to the tab shows current balance.
  //   - Pause polling while the tab is hidden (no point burning request budget).
  useEffect(() => {
    if (authLoading || !user) {
      return undefined;
    }

    let intervalId = null;

    const startPoll = () => {
      if (intervalId !== null) return;
      intervalId = setInterval(() => {
        if (!document.hidden) refresh();
      }, 45_000);
    };

    const stopPoll = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        stopPoll();
      } else {
        refresh();
        startPoll();
      }
    };

    const onFocus = () => refresh();

    if (!document.hidden) {
      startPoll();
    }
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      stopPoll();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [authLoading, user, refresh]);

  const openPaywall = useCallback((reason) => {
    setPaywallReason(reason || null);
    setPaywallOpen(true);
  }, []);

  const closePaywall = useCallback(() => {
    setPaywallOpen(false);
    setPaywallReason(null);
  }, []);

  // Expose imperative hooks to the module-level ref so axios interceptors can
  // call into the provider without React context.
  useEffect(() => {
    _paywallHook.open = openPaywall;
    _paywallHook.refresh = refresh;
    return () => {
      _paywallHook.open = () => {};
      _paywallHook.refresh = () => {};
    };
  }, [openPaywall, refresh]);

  const value = useMemo(
    () => ({
      account,
      ready,
      refresh,
      billingEnabled: account?.billing_enabled ?? false,
      balance: account?.credit_balance ?? 0,
      isExempt: account?.is_exempt ?? false,
      planType: account?.plan_type ?? 'trial',
      status: account?.status ?? 'trialing',
      paywallOpen,
      paywallReason,
      openPaywall,
      closePaywall,
    }),
    [account, ready, refresh, paywallOpen, paywallReason, openPaywall, closePaywall],
  );

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
}

export function useBilling() {
  const ctx = useContext(BillingContext);
  if (!ctx) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return ctx;
}
